import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Trophy, Users, BarChart3, Shield, Zap, Share2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_1ec63aee-8cac-40d0-af9b-2322b1e1b60a/artifacts/17qzkh1t_LOGO%20AECAR%20VIDRIO.png";

export default function Landing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [settings, setSettings] = useState({});
  
  // Form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");

  // Load global settings and check auth
  useEffect(() => {
    let isMounted = true;
    
    // Fetch global settings (non-blocking)
    fetch(`${API}/admin/global-settings`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && isMounted) {
          setSettings(data);
          if (data.landing_background_url) {
            document.body.style.setProperty('--landing-bg', `url(${data.landing_background_url})`);
          }
        }
      })
      .catch(() => {});
    
    // Check auth - only if token exists
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      // No token = not authenticated, skip check
      if (!token) {
        if (isMounted) setCheckingAuth(false);
        return;
      }
      
      try {
        const response = await fetch(`${API}/auth/me`, { 
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          credentials: 'include'
        });
        
        if (response.ok && isMounted) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (e) {
        // Not authenticated - clear invalid token
        localStorage.removeItem('token');
      }
      if (isMounted) setCheckingAuth(false);
    };
    
    checkAuth();
    
    return () => {
      isMounted = false;
      document.body.style.removeProperty('--landing-bg');
    };
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al iniciar sesión');
      }
      
      const data = await response.json();
      localStorage.setItem('token', data.token);
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(String(error?.message || 'Error al iniciar sesión'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: registerEmail, 
          password: registerPassword,
          name: registerName 
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al registrarse');
      }
      
      toast.success('Cuenta creada. Inicia sesión.');
    } catch (error) {
      toast.error(String(error?.message || 'Error al registrarse'));
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  const logoUrl = settings.logo_url || DEFAULT_LOGO;
  const heroImage = settings.landing_hero_image_url || settings.landing_background_url || 'https://images.unsplash.com/photo-1553618737-997fbd5bf9b4?w=1920&q=80';

  return (
    <div 
      className="min-h-screen bg-background noise-overlay"
      style={settings.landing_background_url ? {
        backgroundImage: `url(${settings.landing_background_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      } : {}}
    >
      {settings.landing_background_url && <div className="fixed inset-0 bg-black/70 -z-10" />}
      
      {/* Hero Section */}
      <div 
        className="relative min-h-[60vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="flex flex-col items-center justify-center gap-4 mb-6">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-32 h-32 object-contain"
            />
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-wider">
              Championship Manager
            </h1>
          </div>
          <p className="text-base text-zinc-300 max-w-2xl mx-auto mb-8 font-sans">
            Sistema profesional de gestión de campeonatos AECAR. 
            Control total de pilotos, carreras, puntuaciones y clasificaciones.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-2 text-zinc-400">
              <Trophy className="w-5 h-5 text-primary" />
              <span className="font-sans">4 Carreras</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <BarChart3 className="w-5 h-5 text-primary" />
              <span className="font-sans">Descarte Automático</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-sans">Desempate AECAR</span>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Section */}
      <div className="relative z-10 -mt-16 pb-20">
        <div className="max-w-md mx-auto px-4">
          <Card className="bg-card/95 backdrop-blur-xl border-white/10">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-2xl uppercase">Acceso Delegados</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted">
                  <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="delegado@aecar.es"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Contraseña</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full font-bold uppercase tracking-wide"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Entrando...' : 'Entrar'}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="w-full text-sm text-primary hover:text-primary/80 transition-colors mt-2"
                      data-testid="forgot-password-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </form>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-white/10" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">o continúa con</span>
                    </div>
                  </div>
                  
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full border-white/10 hover:bg-white/5"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </Button>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nombre</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Tu nombre"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        required
                        data-testid="register-name-input"
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="delegado@aecar.es"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        required
                        data-testid="register-email-input"
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Contraseña</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Mínimo 6 caracteres"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="register-password-input"
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full font-bold uppercase tracking-wide"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? 'Creando...' : 'Crear Cuenta'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-card/50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-heading font-black text-3xl text-center uppercase mb-12">
            Funcionalidades
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard 
              icon={<Users className="w-8 h-8" />}
              title="Gestión de Pilotos"
              description="Base de datos completa con licencia, nombre, email y categoría. Importación CSV masiva."
            />
            <FeatureCard 
              icon={<Trophy className="w-8 h-8" />}
              title="Campeonatos Múltiples"
              description="Crea campeonatos independientes (1/8 Gas, Eléctricos, etc.) con 4 carreras cada uno."
            />
            <FeatureCard 
              icon={<BarChart3 className="w-8 h-8" />}
              title="Clasificación Automática"
              description="Cálculo de puntos, descarte del peor resultado y desempate según Art. 18.1 AECAR."
            />
            <FeatureCard 
              icon={<Zap className="w-8 h-8" />}
              title="Puntos de Asistencia"
              description="Bonus opcional: +150 (4 carreras), +100 (3 carreras), +50 (2 carreras)."
            />
            <FeatureCard 
              icon={<Shield className="w-8 h-8" />}
              title="Datos Privados"
              description="Cada delegado solo ve sus campeonatos. Toggle público/privado por clasificación."
            />
            <FeatureCard 
              icon={<Share2 className="w-8 h-8" />}
              title="Exportar y Compartir"
              description="Descarga PDF y envío de resultados por email a todos los pilotos."
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>AECAR Championship Manager © {new Date().getFullYear()}</p>
          <p className="mt-1">Sistema de gestión independiente para delegados</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <Card className="bg-card/50 border-white/10 hover:border-primary/50 transition-colors">
      <CardContent className="pt-6">
        <div className="text-primary mb-4">{icon}</div>
        <h3 className="font-heading font-bold text-lg uppercase mb-2">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </CardContent>
    </Card>
  );
}
